"use client";

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from "react";
import type clevertapSdk from "clevertap-web-sdk";
import {
    canAutoPromptFromLogin,
    CLEVERTAP_PUSH_ASK_AGAIN_SECONDS,
    CLEVERTAP_SW_PATH,
    getCleverTapServiceWorkerRegistration,
    logPushDebugState,
    markPushPromptShown,
    PUSH_PROMPT_LAST_SHOWN_AT_KEY,
    PUSH_PROMPT_SESSION_KEY,
    releasePushRegistrationLock,
    shouldShowPushPromptNow,
    syncNotificationPreferenceWithBrowser,
    tryAcquirePushRegistrationLock,
} from "@/app/utils/push-notification-prompt";
import { isCleverTapWebPushEnabled } from "@/app/utils/clevertap-push-enabled";
import { NotificationPermissionState } from "@/app/enums/notification-permission";

type CleverTapInstance = typeof clevertapSdk;

export interface CleverTapUserProfile {
    name?: string;
    email?: string;
    phone?: string;
    identity?: string | number;
    gender?: "M" | "F";
    dob?: Date;
    [key: string]: unknown;
}

interface CleverTapContextType {
    clevertap: CleverTapInstance | null;
    isInitialized: boolean;
    onUserLogin: (profile: CleverTapUserProfile, promptAfterLogin?: boolean) => void;
    updateProfile: (properties: Record<string, unknown>) => void;
    cleverTapTrackEvent: (eventName: string, properties?: Record<string, unknown>) => void;
    requestPushPermission: () => boolean;
    logout: () => void;
}

const CleverTapContext = createContext<CleverTapContextType>({
    clevertap: null,
    isInitialized: false,
    onUserLogin: () => {},
    updateProfile: () => {},
    cleverTapTrackEvent: () => {},
    requestPushPermission: () => false,
    logout: () => {},
});

const isWebView = (): boolean => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        return false;
    }
    const userAgent = navigator.userAgent || navigator.vendor;
    return /wv|WebView|(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent);
};

type PendingEvent = { eventName: string; properties?: Record<string, unknown> };

function normalizePhoneForCleverTap(phone: string | undefined): string | undefined {
    if (!phone || typeof phone !== "string") return undefined;
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) return undefined;
    let normalized: string;
    if (digits.startsWith("91") && digits.length >= 12) {
        const rest = digits.slice(2).replace(/^0+/, "") || digits.slice(2);
        normalized = `+91${rest}`;
    } else {
        const rest = digits.replace(/^0+/, "") || digits;
        normalized = rest.length >= 10 ? `+91${rest.slice(-10)}` : `+91${rest}`;
    }
    return /^\+\d{10,15}$/.test(normalized) ? normalized : undefined;
}

export const CleverTapProvider = ({ children }: { children: ReactNode }) => {
    const [clevertap, setClevertap] = useState<CleverTapInstance | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const pendingEventsRef = useRef<PendingEvent[]>([]);
    const clevertapIdentityRef = useRef<string | number | null>(null);
    const pendingPushAfterIdentityRef = useRef(false);

    const invokeCleverTapPushPrompt = useCallback(
        (source: string): boolean => {
            if (!isInitialized || !clevertap) {
                console.warn("CleverTap not initialized. Cannot request push permission.");
                return false;
            }

            if (!clevertapIdentityRef.current) {
                pendingPushAfterIdentityRef.current = true;
                if (process.env.NODE_ENV === "development") {
                    console.log("[PushDebug] Queued push prompt until identity is set", { source });
                }
                return false;
            }

            if (!shouldShowPushPromptNow()) {
                syncNotificationPreferenceWithBrowser();
                void logPushDebugState(`${source}:skipped-guard`, clevertapIdentityRef.current);
                return false;
            }

            if (!tryAcquirePushRegistrationLock()) {
                if (process.env.NODE_ENV === "development") {
                    console.log("[PushDebug] Push registration already in flight", { source });
                }
                return false;
            }

            void logPushDebugState(`${source}:before-prompt`, clevertapIdentityRef.current);

            try {
                clevertap.notifications.push({
                    titleText: "Enable browser notifications",
                    bodyText: "Get alerts for order updates, opportunities, and important account activity.",
                    okButtonText: "Allow",
                    rejectButtonText: "No, thanks",
                    okButtonColor: "#F28046",
                    askAgainTimeInSeconds: CLEVERTAP_PUSH_ASK_AGAIN_SECONDS,
                    serviceWorkerPath: CLEVERTAP_SW_PATH,
                });
                markPushPromptShown();
                void logPushDebugState(`${source}:prompt-invoked`, clevertapIdentityRef.current);
                return true;
            } finally {
                releasePushRegistrationLock();
            }
        },
        [clevertap, isInitialized],
    );

    const flushPendingPushPrompt = useCallback(
        (source: string) => {
            if (!pendingPushAfterIdentityRef.current) return;
            pendingPushAfterIdentityRef.current = false;
            invokeCleverTapPushPrompt(source);
        },
        [invokeCleverTapPushPrompt],
    );

    useEffect(() => {
        if (typeof window === "undefined") return;

        if (isWebView() || (window as unknown as { ReactNativeWebView?: unknown }).ReactNativeWebView) {
            console.warn("WebView detected — skipping CleverTap initialization.");
            return;
        }

        if (process.env.NEXT_PUBLIC_CLEVERTAP_ENABLED !== "true") {
            console.log("CleverTap is disabled via environment variable.");
            return;
        }

        const accountId = process.env.NEXT_PUBLIC_CLEVERTAP_ACCOUNT_ID;
        const region = process.env.NEXT_PUBLIC_CLEVERTAP_REGION;

        if (!accountId) {
            console.error("CleverTap Account ID is missing. Please set NEXT_PUBLIC_CLEVERTAP_ACCOUNT_ID.");
            return;
        }

        import("clevertap-web-sdk")
            .then((module) => {
                const ct = module.default as CleverTapInstance;

                ct.init(accountId, region);
                ct.spa = true;
                ct.privacy.push({ optOut: false });
                ct.privacy.push({ useIP: false });

                if (process.env.NODE_ENV === "development") {
                    ct.setLogLevel(3);
                }

                const toFlush = pendingEventsRef.current.splice(0);
                toFlush.forEach(({ eventName, properties }) => {
                    if (properties) ct.event.push(eventName, properties);
                    else ct.event.push(eventName);
                });

                setClevertap(ct);
                setIsInitialized(true);

                if (process.env.NEXT_PUBLIC_CLEVERTAP_PUSH_ENABLED === "true") {
                    void getCleverTapServiceWorkerRegistration().then((registration) => {
                        if (registration && process.env.NODE_ENV === "development") {
                            console.log("CleverTap Service Worker ready:", registration.scope);
                        }
                    });
                }
            })
            .catch((error) => {
                console.error("Failed to load CleverTap SDK:", error);
            });
    }, []);

    const onUserLogin = useCallback(
        (profile: CleverTapUserProfile, promptAfterLogin = false) => {
            if (!isInitialized || !clevertap) {
                console.warn("CleverTap not initialized. Cannot call onUserLogin.");
                return;
            }

            const phoneForCleverTap = normalizePhoneForCleverTap(profile.phone);
            clevertap.onUserLogin.push({
                Site: {
                    Name: profile.name,
                    Identity: profile.identity,
                    Email: profile.email,
                    ...(phoneForCleverTap !== undefined && { Phone: phoneForCleverTap }),
                    Gender: profile.gender,
                    DOB: profile.dob,
                    "MSG-email": true,
                    "MSG-push": isCleverTapWebPushEnabled(),
                    "MSG-sms": true,
                    "MSG-whatsapp": true,
                },
            });

            clevertapIdentityRef.current = profile.identity ?? null;
            void logPushDebugState("onUserLogin", clevertapIdentityRef.current);

            if (!isCleverTapWebPushEnabled()) return;

            const permission = syncNotificationPreferenceWithBrowser();
            if (permission !== NotificationPermissionState.DEFAULT) return;

            if (promptAfterLogin && canAutoPromptFromLogin()) {
                invokeCleverTapPushPrompt("onUserLogin");
                return;
            }

            flushPendingPushPrompt("onUserLogin:pending");
        },
        [clevertap, isInitialized, invokeCleverTapPushPrompt, flushPendingPushPrompt],
    );

    const updateProfile = useCallback(
        (properties: Record<string, unknown>) => {
            if (!isInitialized || !clevertap) {
                console.warn("CleverTap not initialized. Cannot update profile.");
                return;
            }

            clevertap.profile.push({
                Site: properties,
            });
        },
        [clevertap, isInitialized],
    );

    const cleverTapTrackEvent = useCallback(
        (eventName: string, properties?: Record<string, unknown>) => {
            if (!isInitialized || !clevertap) {
                pendingEventsRef.current.push({ eventName, properties });
                return;
            }

            if (properties) {
                clevertap.event.push(eventName, properties);
            } else {
                clevertap.event.push(eventName);
            }
        },
        [clevertap, isInitialized],
    );

    const requestPushPermission = useCallback(() => {
        if (!isCleverTapWebPushEnabled()) {
            return false;
        }

        if (typeof window === "undefined" || !("Notification" in window)) {
            console.warn("Notification API unavailable. Cannot request push permission.");
            return false;
        }

        return invokeCleverTapPushPrompt("requestPushPermission");
    }, [invokeCleverTapPushPrompt]);

    const logout = useCallback(() => {
        clevertapIdentityRef.current = null;
        pendingPushAfterIdentityRef.current = false;
        releasePushRegistrationLock();

        if (typeof window !== "undefined") {
            sessionStorage.removeItem(PUSH_PROMPT_SESSION_KEY);
            localStorage.removeItem(PUSH_PROMPT_LAST_SHOWN_AT_KEY);
        }

        if (!isInitialized || !clevertap) {
            return;
        }

        clevertap.logout();
        if (process.env.NODE_ENV === "development") {
            console.log("CleverTap: User logged out");
        }
    }, [clevertap, isInitialized]);

    return (
        <CleverTapContext.Provider
            value={{
                clevertap,
                isInitialized,
                onUserLogin,
                updateProfile,
                cleverTapTrackEvent,
                requestPushPermission,
                logout,
            }}
        >
            {children}
        </CleverTapContext.Provider>
    );
};

export const useCleverTap = () => useContext(CleverTapContext);
